/** @jsxImportSource @emotion/react */

import React, { useState } from 'react';
import styled from 'styled-components';
import * as color from '@constant/color';
import * as size from '@constant/size';
import TextBlock from '@component/common/block/text-block';
import TextButton from '@design-system/text-button';
import Gap from '@component/common/gap';
import ModalTemplate from '@component/common/modal/modal-template';
import { ETheme } from '@datatype/type';

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  width: ${size.MEDIUM_MODAL_WIDTH};
`;

const NoteContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
  padding: ${size.MARGIN_1};
  border-radius: ${size.BORDER_RADIUS_1};
  background-color: ${color.GRAY_BACKGROUND_COLOR};
`;

interface IProps {
  releaseVersion?: string | null;
  releaseNote?: string | null;
  handleRefresh: () => void;
}

/**
 * 새 버전 배포 감지 시 노출되는 모달
 * - 닫기 불가(오버레이 클릭/X 버튼 비활성) — 구버전 캐시로 인한 오류를 막기 위해 강제 업데이트
 * - 버튼 클릭 시 localStorage 정리 + cache 삭제 후 재진입 (useWebVersionCheck.handleRefresh)
 */
const WebRefreshModal: React.FC<IProps> = ({ releaseVersion, releaseNote, handleRefresh }) => {
  const [isLoading, setIsLoading] = useState(false);

  // '- 항목' 줄바꿈 구분 → 라인 배열 (빈 줄 제거)
  const noteLines = (releaseNote ?? '')
    .split('\n')
    .map((line) => line.replace(/^-\s*/, '').trim())
    .filter((line) => line.length > 0);

  const handleClick = () => {
    if (isLoading) return;
    // 정리 + 이동이 끝날 때까지 버튼 로딩 유지
    setIsLoading(true);
    handleRefresh();
  };

  return (
    <ModalTemplate hideCloseButton closeOnBackgroundClick={false}>
      <Wrapper>
        <TextBlock
          text={'새로운 버전이 나왔어요'}
          design={{
            text: { size: 'large', color: color.BLACK_2, bold: true },
          }}
        />
        <Gap height={'5px'} />
        <TextBlock
          text={'새로고침하고 이어서 학습해요'}
          design={{
            text: { size: 'medium', color: color.GRAY_1, bold: false },
          }}
        />
        {(releaseVersion || noteLines.length > 0) && (
          <>
            <Gap height={size.MARGIN_1} />
            <NoteContainer>
              {releaseVersion && (
                <TextBlock
                  text={`v${releaseVersion}`}
                  design={{
                    text: { size: 'small', color: color.BLACK_2, bold: true },
                  }}
                />
              )}
              {noteLines.map((line) => (
                <TextBlock
                  key={line}
                  text={`· ${line}`}
                  design={{
                    text: { size: 'small', color: color.GRAY_1, bold: false },
                  }}
                />
              ))}
            </NoteContainer>
          </>
        )}
        <Gap height={size.MARGIN_2} />
        <TextButton
          theme={ETheme.BROWN}
          kind={'filled'}
          size={'medium'}
          shape='pill'
          width={'100%'}
          text={'새로고침'}
          isLoading={isLoading}
          handleClick={handleClick}
        />
      </Wrapper>
    </ModalTemplate>
  );
};

export default WebRefreshModal;
